import React, {useContext, useEffect, useState} from 'react';
import {Alert, Button, Col, Form, Input, Modal, Select, Spin, Table} from 'antd';
import {ClinicResponse, ClinicCreate, ClinicUpdate} from '../../api-client';
import {FormattedMessage, useIntl} from 'react-intl';
import {CaretDownOutlined, DeleteOutlined, EditOutlined} from "@ant-design/icons";
import {accessibilityRoles} from "../../utils/accessUtils";
import AccessControl from "../common/AccessControl";
import ClinicSearchForm from "./ClinicSearchFrom";
import {getClinicsApi} from "../../api/apiInstances";
import logger from "../../utils/logger";
import {TIMEZONE_OPTIONS} from "../../utils/timezoneUtils";
import {AlertContext} from "../../context/AlertContext";

interface RoomItem {
    id: string;
    name: string;
}

const Clinics = () => {
    const intl = useIntl();
    const clinicsApi = getClinicsApi();
    const {setSuccessMessage, setVisible} = useContext(AlertContext);
    const [form] = Form.useForm();
    const [rooms, setRooms] = useState<ClinicResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [clinicToDelete, setClinicToDelete] = useState<string>();
    const [clinicToEdit, setClinicToEdit] = useState<string | undefined>();
    const [keyword, setKeyword] = useState<string>('');
    const [displayedRooms, setDisplayedRooms] = useState<ClinicResponse[]>([]);

    const showAddModal = () => {
        form.resetFields();
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const validTz = TIMEZONE_OPTIONS.some(opt => opt.value === browserTz) ? browserTz : 'UTC';
        form.setFieldsValue({timezone: validTz});
        setIsAddModalVisible(true);
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = () => {
        clinicsApi.listClinics()
            .then(response => {
                setRooms(response.data);
                setDisplayedRooms(response.data);
                setLoading(false);
            })
            .catch(error => {
                logger.error("Failed to load rooms:", error);
                setLoading(false);
            });
    }

    useEffect(() => {
        if (keyword) {
            const filteredRooms = rooms.filter(room => room.name?.toLowerCase().includes(keyword.toLowerCase()));
            setDisplayedRooms(filteredRooms);
        } else {
            setDisplayedRooms(rooms);
        }
    }, [rooms, keyword]);

    const handleDeleteClinic = async (index: string) => {
        try {
            await clinicsApi.deleteClinic(index);
            setSuccessMessage(intl.formatMessage({id: 'clinic_deleted'}));
            setVisible(true);
            loadData();
        } catch (error: any) {
            if (error?.response?.status === 404) {
                // Already deleted — silently refresh list
                loadData();
            } else {
                logger.error("Failed to delete clinic:", error);
            }
        }
    };

    const handleEditClinic = (roomId: string | undefined) => {
        const roomToEdit = rooms.find(room => room.id === roomId);
        if (roomToEdit) {
            setClinicToEdit(roomToEdit.id ?? '');
            form.resetFields();
            form.setFieldsValue({
                name: roomToEdit.name ?? '',
                address: roomToEdit.address ?? '',
                timezone: roomToEdit.timezone ?? 'UTC',
            });
            setIsEditModalVisible(true);
        }
    };

    const handleDeleteOk = () => {
        if (clinicToDelete !== undefined) {
            handleDeleteClinic(clinicToDelete);
            setIsDeleteModalVisible(false);
        }
    };

    const showDeleteModal = (index: string) => {
        setClinicToDelete(index);
        setIsDeleteModalVisible(true);
    };

    const handleCreateRoom = async (values: {name: string; address?: string; timezone: string}) => {
        const cabinetToCreate: ClinicCreate = {
            name: values.name.trim(),
            address: values.address ?? '',
            timezone: values.timezone,
        };
        try {
            await clinicsApi.createClinic(cabinetToCreate);
            loadData();
        } catch (error) {
            logger.error("Failed to create room:", error);
        }
    };

    const handleAddOk = async () => {
        try {
            const values = await form.validateFields();
            await handleCreateRoom(values);
            form.resetFields();
            setIsAddModalVisible(false);
        } catch {
            // validation failed — stay open so user can correct errors
        }
    };

    const handleEditOk = async () => {
        if (!clinicToEdit) return;
        try {
            const values = await form.validateFields();
            const updatedRoom: ClinicUpdate = {
                name: values.name.trim(),
                address: values.address,
                timezone: values.timezone,
            };
            await clinicsApi.updateClinic(clinicToEdit, updatedRoom);
            loadData();
            form.resetFields();
            setIsEditModalVisible(false);
        } catch (error: any) {
            if (error?.errorFields) return; // antd validation — stay open
            logger.error("Failed to update room:", error);
        }
    };

    const handleCancel = () => {
        setIsAddModalVisible(false);
        setIsEditModalVisible(false);
        setIsDeleteModalVisible(false);
        form.resetFields();
    };

    const onAddButtonClick = () => {
        showAddModal();
    };

    const modalContent = (
        <div className="medical-history-modal-group">
            <Form form={form} layout="vertical">
                <Form.Item
                    className="dentobook-form-item dentobook-simple-input"
                    label={<FormattedMessage id="name"/>}
                    name="name"
                    rules={[
                        {required: true, whitespace: true, message: intl.formatMessage({id: 'required_field'})},
                        {min: 2, message: intl.formatMessage({id: 'clinic_name_min_length'})},
                    ]}
                >
                    <Input/>
                </Form.Item>
                <Form.Item
                    className="dentobook-form-item dentobook-simple-input"
                    label={<FormattedMessage id="address"/>}
                    name="address"
                >
                    <Input/>
                </Form.Item>
                <Form.Item
                    className="dentobook-form-item dentobook-simple-input"
                    label={<FormattedMessage id="clinicTimezone"/>}
                    name="timezone"
                    rules={[{required: true, message: intl.formatMessage({id: 'required_field'})}]}
                    tooltip={intl.formatMessage({id: 'clinicTimezoneHint'})}
                >
                    <Select
                        options={TIMEZONE_OPTIONS}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        suffixIcon={<CaretDownOutlined/>}
                        size="large"
                        className="dentobook-input"
                    />
                </Form.Item>
            </Form>
        </div>
    );

    return (

        <AccessControl
            accessibleFor={[accessibilityRoles.CLINIC_ADMIN, accessibilityRoles.MEDICAL_DOCTOR, accessibilityRoles.MEDICAL_ASSISTANT, accessibilityRoles.RECEPTIONIST, accessibilityRoles.DENTAL_TECHNICIAN]}>
            <Col>
                <ClinicSearchForm
                    keyword={keyword}
                    setKeyword={setKeyword}
                    loadData={loadData}
                    onAddButtonClick={onAddButtonClick}
                />
            </Col>
            <Alert
                message={intl.formatMessage({id: 'clinicsInfoMessage'})}
                type="info"
                showIcon
                style={{marginBottom: 16}}
            />
            {loading ? (
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <Spin tip={<FormattedMessage id="loading" />} />
                </div>
            ) : (
            <Table dataSource={displayedRooms}
                   pagination={false}
                   className="clinics-table">
                <Table.Column key="name-column"
                              className={'custom-column-style'}
                              title={<FormattedMessage id="clinics"/>}
                              render={(room) => <span key={room.name} className="font-weight-500">{room.name}</span>}
                              sorter={(roomOne: RoomItem, roomTwo: RoomItem) => roomOne.name.localeCompare(roomTwo.name)}
                />
                <Table.Column key="actions-column"
                              title={<div></div>}
                              className={'custom-column-style'}
                              render={(room) => {
                                  return (
                                      <div style={{display: 'unset', alignItems: 'right', float: 'right'}}>
                                          <Button key={room.id + "-edit"}
                                                  className="text-black font-20 ml-1" type="link"
                                                  icon={<EditOutlined/>}
                                                  onClick={() => handleEditClinic(room.id)}
                                                  style={{marginRight: '10px'}}/>
                                          <Button key={room.id + "-delete"}
                                                  className="text-black font-20" type="link"
                                                  icon={<DeleteOutlined/>}
                                                  onClick={() => showDeleteModal(room.id as string)}/>
                                      </div>
                                  );
                              }}
                />
            </Table>
            )}
            <Modal
                transitionName=""
                destroyOnClose
                okButtonProps={{
                    className: "modal-btn"
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }} title={<FormattedMessage id="addClinic"/>} okText={intl.formatMessage({id: 'save'})}
                cancelText={intl.formatMessage({id: 'cancel'})} open={isAddModalVisible} onOk={handleAddOk}
                onCancel={handleCancel}
                className="dentobook-modal">
                {modalContent}
            </Modal>
            <Modal
                transitionName=""
                destroyOnClose
                okButtonProps={{
                    className: "modal-btn"
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                title={<FormattedMessage id="editClinic"/>} okText={intl.formatMessage({id: 'save'})}
                cancelText={intl.formatMessage({id: 'cancel'})} open={isEditModalVisible} onOk={handleEditOk}
                onCancel={handleCancel}
                className="dentobook-modal">
                {modalContent}
            </Modal>
            <Modal
                transitionName=""
                okButtonProps={{
                    className: "modal-btn",
                    danger: true,
                    type: "primary",
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                className="dentobook-modal"
                title={<FormattedMessage id="deleteClinic"/>}
                okText={intl.formatMessage({id: 'delete'})}
                cancelText={intl.formatMessage({id: 'cancel'})}
                open={isDeleteModalVisible}
                onOk={handleDeleteOk}
                onCancel={handleCancel}
            >
                <p><FormattedMessage id="clinicDeleteConfirmation"/></p>
                <Alert
                    type="warning"
                    showIcon
                    message={<FormattedMessage id="clinicDeleteWarningTitle"/>}
                    description={<FormattedMessage id="clinicDeleteWarningHint"/>}
                    style={{marginTop: 12}}
                />
            </Modal>


        </AccessControl>
    );
};

export default Clinics;
